import _capitalize from 'lodash/capitalize';

import { DateFormat, formatDate } from '@lyearn/core/utils/date/formatDate';
import { TFunction } from '@lyearn/i18n';
import { customEmailDigestsById } from '@/components/Form/components/Frequency/components/Custom/conts';
import { daysOfWeekById, OrderedMonthsOfYear } from '@/components/Form/components/Frequency/conts';
import { convertTo12HourFormat } from '@/components/Form/components/Frequency/utils';
import {
  CronFrequencyEnum,
  CustomFrequencyEnum,
  DayOfWeekEnum,
  Maybe,
  ScheduleInfo,
  WeekOfMonthEnum,
} from '@/types/schema';

const getYearFromDate = (date?: Maybe<string>) => {
  // choosing a leap year to avoid any issues with date formatting
  if (!date) return 2024;
  const dateParts = date.split(' ');
  const year = dateParts[3];

  return Number(year);
};

const DAY_OF_WEEK = {
  [DayOfWeekEnum.Monday]: 'Common:Weekdays.Monday',
  [DayOfWeekEnum.Tuesday]: 'Common:Weekdays.Tuesday',
  [DayOfWeekEnum.Wednesday]: 'Common:Weekdays.Wednesday',
  [DayOfWeekEnum.Thursday]: 'Common:Weekdays.Thursday',
  [DayOfWeekEnum.Friday]: 'Common:Weekdays.Friday',
  [DayOfWeekEnum.Saturday]: 'Common:Weekdays.Saturday',
  [DayOfWeekEnum.Sunday]: 'Common:Weekdays.Sunday',
};

export const FREQUENCY_TRANSLATION_KEYS: {
  [key in CronFrequencyEnum]: string;
} = {
  [CronFrequencyEnum.Daily]: 'Common:Frequency.Daily',
  [CronFrequencyEnum.Weekly]: 'Common:Frequency.Weekly',
  [CronFrequencyEnum.Monthly]: 'Common:Frequency.Monthly',
  [CronFrequencyEnum.Quarterly]: 'Common:Frequency.Quarterly',
  [CronFrequencyEnum.Yearly]: 'Common:Frequency.Yearly',
  [CronFrequencyEnum.Fortnightly]: 'Common:Frequency.Fortnightly',
  [CronFrequencyEnum.Custom]: 'Common:Frequency.Custom',
  [CronFrequencyEnum.Hourly]: 'Common:Frequency.Hourly',
};

const WEEKOFMONTH_TRANSLATION_KEYS: {
  [key in WeekOfMonthEnum]: string;
} = {
  [WeekOfMonthEnum.First]: 'Common:WeekOfMonth.First',
  [WeekOfMonthEnum.Second]: 'Common:WeekOfMonth.Second',
  [WeekOfMonthEnum.Third]: 'Common:WeekOfMonth.Third',
  [WeekOfMonthEnum.Fourth]: 'Common:WeekOfMonth.Fourth',
  [WeekOfMonthEnum.Last]: 'Common:WeekOfMonth.Last',
};

const getWeeksOfMonthString = (t: TFunction, weeksOfMonth?: WeekOfMonthEnum[] | null) => {
  if (!weeksOfMonth?.length) {
    return '';
  }
  const labels = weeksOfMonth.map((value) => t(WEEKOFMONTH_TRANSLATION_KEYS[value]));
  if (labels.length === 1) {
    return labels[0];
  }

  return `${labels.slice(0, -1).join(', ')} ${t('Common:And')} ${labels.slice(-1)[0]}`;
};

const getCustomIntervalString = (
  interval: number,
  emailDigest: { label: string; pluralLabel: string },
) => {
  return interval <= 1
    ? _capitalize(emailDigest.label)
    : `${interval} ${_capitalize(emailDigest.pluralLabel)}`;
};

const handleSingleAndMultipleValues = <T extends string>(
  single: Maybe<T> | undefined,
  multiple: Maybe<T[]> | undefined,
) => (multiple ? multiple : single ? [single] : null);

const getCustomFrequencyDescString = (t: TFunction, schedule: ScheduleInfo) => {
  const { custom, time, startDate } = schedule;

  if (!custom) return '';

  const { frequency, dayOfMonth, monthOfYear, daysOfWeek, interval } = custom;
  const weeksOfMonth = handleSingleAndMultipleValues(custom.weekOfMonth, custom.weeksOfMonth);

  const intervalString = getCustomIntervalString(
    interval,
    customEmailDigestsById[custom.frequency],
  );
  const translatedShortLabelOfDaysOfWeek = daysOfWeek
    ?.map((it) => t(daysOfWeekById[it].shortLabelKey))
    .join(', ');
  const scheduleTime = convertTo12HourFormat(time);
  const translatedWeeksOfMonth = getWeeksOfMonthString(t, weeksOfMonth);

  const dayOfMonthFormatted =
    dayOfMonth && monthOfYear
      ? formatDate(
          new Date(
            getYearFromDate(startDate),
            OrderedMonthsOfYear.findIndex((moy) => moy === monthOfYear),
            dayOfMonth,
          ),
          DateFormat.Date,
        )
      : '';

  const translatedKeys = {
    intervalString,
    time: scheduleTime,
    daysOfWeek: translatedShortLabelOfDaysOfWeek,
    dayOfMonth: dayOfMonthFormatted,
    weeksOfMonth: translatedWeeksOfMonth,
  };

  switch (frequency) {
    case CustomFrequencyEnum.Day: {
      return t('Common:CustomFrequency.Day', translatedKeys);
    }
    case CustomFrequencyEnum.Week: {
      return t('Common:CustomFrequency.Week', translatedKeys);
    }
    case CustomFrequencyEnum.Month: {
      return t('Common:CustomFrequency.Month', translatedKeys);
    }
    case CustomFrequencyEnum.Year: {
      return t('Common:CustomFrequency.Year', translatedKeys);
    }
  }
};

export const getDescString = (t: TFunction, schedule: ScheduleInfo) => {
  const { frequency, time, dayOfMonth, MonthOfYear, startDate } = schedule;

  const daysOfWeek = handleSingleAndMultipleValues(schedule.dayOfWeek, schedule.daysOfWeek);
  const weeksOfMonth = handleSingleAndMultipleValues(schedule.weekOfMonth, schedule.weeksOfMonth);

  const translatedFrequency = t(FREQUENCY_TRANSLATION_KEYS[frequency]);
  const twelveHourTime = convertTo12HourFormat(time);
  const translatedDaysOfWeek = daysOfWeek?.map((it) => t(daysOfWeekById[it].labelKey)).join(',');
  const translatedWeeksOfMonth = getWeeksOfMonthString(t, weeksOfMonth);
  const dayOfMonthFormatted =
    dayOfMonth && MonthOfYear
      ? formatDate(
          new Date(
            getYearFromDate(startDate),
            OrderedMonthsOfYear.findIndex((moy) => moy === MonthOfYear),
            dayOfMonth,
          ),
          DateFormat.Date,
        )
      : '';

  const translatedKeys = {
    frequency: translatedFrequency,
    time: twelveHourTime,
    dayOfMonth: dayOfMonthFormatted,
    daysOfWeek: translatedDaysOfWeek,
    weeksOfMonth: translatedWeeksOfMonth,
  };

  switch (frequency) {
    case CronFrequencyEnum.Daily:
      return t('Common:CronFrequency.Daily', translatedKeys);
    case CronFrequencyEnum.Weekly:
      return t('Common:CronFrequency.Weekly', translatedKeys);
    case CronFrequencyEnum.Monthly:
      return t('Common:CronFrequency.Monthly', translatedKeys);
    case CronFrequencyEnum.Quarterly:
      return t('Common:CronFrequency.Quarterly', translatedKeys);
    case CronFrequencyEnum.Yearly:
      return t('Common:CronFrequency.Yearly', translatedKeys);
    case CronFrequencyEnum.Fortnightly:
      return t('Common:CronFrequency.Fortnightly', translatedKeys);
    case CronFrequencyEnum.Custom:
      return getCustomFrequencyDescString(t, schedule);
    default:
      return '';
  }
};
